//  ┌─ TABLE TAB ─────────────────────────────────────────────────────────┐
//  │  Width: 100%, Percent    → OOXML tblW w="5000" type="pct"           │
//  │  Alignment: Left         → OOXML jc val="left"                      │
//  │  Indent from left: 0 twips→ OOXML tblInd (only if explicitly wrong) │
//  │  Text wrapping: None     → OOXML remove tblpPr / tblOverlap         │
//  └─────────────────────────────────────────────────────────────────────┘
//  ┌─ OPTIONS TAB ───────────────────────────────────────────────────────┐
//  │  Top/Bottom margin: 0 twips→ OOXML tblCellMar (only if block exists)│
//  │  Left/Right margin: 0.08" (115 twips)→ OOXML tblCellMar            │
//  │  No cell spacing         → OOXML remove tblCellSpacing               │
//  │  Autofit ✓               → OOXML tblLayout (only if explicitly wrong)│
//  └─────────────────────────────────────────────────────────────────────┘
//  ┌─ ROW TAB ───────────────────────────────────────────────────────────┐
//  │  No row height           → OOXML remove trHeight                     │
//  │  No row cell spacing     → OOXML remove tblCellSpacing               │
//  │  Header rows             → SKIPPED (tblHeader present)               │

/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 26: Table Defaults  (config-driven)
// ═══════════════════════════════════════════════════════════════════════════

import { safeAlert, reportResult, rangeDetail, RunDetail, SRS_NAV_PREFIX } from '../runs/common';
import { getConfig } from '../taskpane/configService';

// ── pt → twips (1pt = 20 twips) ─────────────────────────────────────────
function ptToTwips(pt: number): string {
  return String(Math.round(pt * 20));
}

// ─── XML helpers ──────────────────────────────────────────────────────────────

function rmSelfClose(xml: string, tag: string): string {
  // S7780: use String.raw to avoid escaping backslashes
  return xml.replace(new RegExp(String.raw`<w:${tag}\b[^>]*/\s*>`, 'g'), '');
}

function rmPaired(xml: string, tag: string): string {
  // S7780: use String.raw
  return xml.replace(new RegExp(String.raw`<w:${tag}\b[^>]*>[\s\S]*?<\/w:${tag}>`, 'g'), '');
}

function rmTag(xml: string, tag: string): string {
  return rmPaired(rmSelfClose(xml, tag), tag);
}

function setElement(
  xml: string, tag: string,
  attrs: Record<string, string>,
  parentClose: string,
  dryRun = false
): { xml: string; changed: boolean } {
  // S7780: use String.raw
  const selfCloseRe = new RegExp(String.raw`<w:${tag}\b[^>]*/\s*>`);
  const pairedRe    = new RegExp(String.raw`<w:${tag}\b[^>]*>\s*</w:${tag}>`);

  // S6594: use RegExp.exec() instead of String.match()
  const match = selfCloseRe.exec(xml) ?? pairedRe.exec(xml);

  if (!match) return { xml, changed: false };

  const existing = match[0];
  // S7780 + S6594: String.raw + exec()
  const allMatch = Object.entries(attrs).every(([k, v]) =>
    new RegExp(String.raw`w:${k}="${v}"`).exec(existing) !== null
  );
  if (allMatch) return { xml, changed: false };

  if (dryRun) return { xml, changed: true };

  const attrStr = Object.entries(attrs).map(([k, v]) => `w:${k}="${v}"`).join(' ');
  return { xml: xml.replace(existing, `<w:${tag} ${attrStr}/>`), changed: true };
}

function setLayoutType(xml: string, layoutType: string, dryRun = false): { xml: string; changed: boolean } {
  // S7780 + S6594
  if (!/<w:tblLayout\b/.test(xml) || new RegExp(String.raw`w:type="${layoutType}"`).exec(xml) !== null) {
    return { xml, changed: false };
  }

  if (dryRun) return { xml, changed: true };

  let cleaned = xml
    .replace(/<w:tblLayout\b[^>]*>[\s\S]*?<\/w:tblLayout>/g, '')
    .replace(/<w:tblLayout\b[^>]*\/\s*>/g, '');

  if (/<w:tblCellMar[\s>]/.test(cleaned)) {
    return {
      xml: cleaned.replace(/<w:tblCellMar([\s>])/, `<w:tblLayout w:type="${layoutType}"/><w:tblCellMar$1`),
      changed: true,
    };
  }
  return {
    xml: cleaned.replace('</w:tblPr>', `<w:tblLayout w:type="${layoutType}"/></w:tblPr>`),
    changed: true,
  };
}

function getMarginValues(block: string): { top: string; bottom: string; left: string; right: string; usesStartEnd: boolean } | null {
  // S6594: use exec()
  const mar = /<w:tblCellMar\b[^>]*>[\s\S]*?<\/w:tblCellMar>/.exec(block)?.[0];
  if (!mar) return null;

  const usesStartEnd = /<w:start\b/.test(mar) || /<w:end\b/.test(mar);

  function side(name: string, alias?: string): string {
    // S7780 + S6594
    const m = new RegExp(String.raw`<w:${name}\b[^>]*\bw:w="([^"]+)"`, 'i').exec(mar);
    if (m) return m[1];
    if (alias) {
      const m2 = new RegExp(String.raw`<w:${alias}\b[^>]*\bw:w="([^"]+)"`, 'i').exec(mar);
      if (m2) return m2[1];
    }
    return '0';
  }
  return {
    top:    side('top'),
    bottom: side('bottom'),
    left:   side('left', 'start'),
    right:  side('right', 'end'),
    usesStartEnd,
  };
}

function getWidthInfo(block: string): { type: string | null; w: string | null } {
  // S6594: use exec()
  const tblW = /<w:tblW\b[^>]*(?:\/>|>[\s\S]*?<\/w:tblW>)/.exec(block)?.[0];
  if (!tblW) return { type: null, w: null };

  // S6582: use optional chaining; S6594: use exec()
  return {
    type: /w:type="([^"]+)"/.exec(tblW)?.[1] ?? null,
    w:    /w:w="([^"]+)"/.exec(tblW)?.[1] ?? null,
  };
}

/**
 * Convert a twips count to inches for human-readable display in comments
 * and logs. (1 inch = 1440 twips.) The OOXML itself always stores twips —
 * this helper is presentation-only.
 */
function twipsToInches(twips: string | number): string {
  // S7773: use Number.parseInt
  const n = typeof twips === 'string' ? Number.parseInt(twips, 10) : twips;
  if (!Number.isFinite(n)) return '?';
  const inches = n / 1440;
  return inches.toFixed(2).replace(/\.?0+$/, '') || '0';
}

interface Run26Config {
  defaultFont:      string;
  defaultSize:      number;
  cellPadding:      { top: number; bottom: number; left: number; right: number };
  tableWidthPct:    number;
  tableAlignment:   string;
  tableIndentTwips: number;
  layoutType:       string;
  // Kept as string to match the upstream TableDefaultsConfig type; cast to
  // Word.InsertLocation only at the single call-site that needs it.
  ooxmlInsertMode:  string;
}

// ─── patchTblPr sub-functions (S3776: reduce cognitive complexity) ────────────

interface PatchCtx {
  changes: string[];
  xml: string;
}

function patchWidth(ctx: PatchCtx, c: Run26Config, dryRun: boolean): void {
  const { type: wType, w: wVal } = getWidthInfo(ctx.xml);
  if (wType === 'pct' && wVal === String(c.tableWidthPct)) return;

  const note = wType === 'dxa' && wVal
    ? `Width → ${c.tableWidthPct / 50}% (was ${twipsToInches(wVal)}")`
    : `Width → ${c.tableWidthPct / 50}% (was ${wType ?? 'unknown'}/${wVal ?? ''})`;
  ctx.changes.push(note);

  if (dryRun) return;

  const target    = `<w:tblW w:w="${c.tableWidthPct}" w:type="pct"/>`;
  // S6594: exec()
  const tblWMatch = /<w:tblW\b[^>]*(?:\/>|>[\s\S]*?<\/w:tblW>)/.exec(ctx.xml);
  if (tblWMatch) {
    ctx.xml = ctx.xml.replace(tblWMatch[0], target);
  } else {
    ctx.xml = ctx.xml.replace(/(<w:tblPr\b[^>]*>)/, `$1${target}`);
  }
}

function patchJc(ctx: PatchCtx, c: Run26Config, dryRun: boolean): void {
  // S6594: exec()
  const jcMatch = /<w:jc\b[^>]*(?:\/>|>[\s\S]*?<\/w:jc>)/.exec(ctx.xml);
  // S7780 + S6594
  if (!jcMatch || new RegExp(String.raw`w:val="${c.tableAlignment}"`).exec(jcMatch[0]) !== null) return;

  ctx.changes.push(`Alignment → ${c.tableAlignment}`);
  if (!dryRun) ctx.xml = ctx.xml.replace(jcMatch[0], `<w:jc w:val="${c.tableAlignment}"/>`);
}

function patchIndent(ctx: PatchCtx, c: Run26Config, dryRun: boolean): void {
  const r = setElement(ctx.xml, 'tblInd', { w: String(c.tableIndentTwips), type: 'dxa' }, '</w:tblPr>', dryRun);
  if (!r.changed) return;
  ctx.changes.push(`Indent → ${twipsToInches(c.tableIndentTwips)}"`);
  if (!dryRun) ctx.xml = r.xml;
}

function patchWrapping(ctx: PatchCtx, dryRun: boolean): void {
  if (/<w:tblpPr\b/.test(ctx.xml)) {
    ctx.changes.push('Text wrapping → None');
    if (!dryRun) ctx.xml = rmTag(ctx.xml, 'tblpPr');
  }
  if (/<w:tblOverlap\b/.test(ctx.xml)) {
    ctx.changes.push('Overlap removed');
    if (!dryRun) ctx.xml = rmTag(ctx.xml, 'tblOverlap');
  }
}

function patchCellSpacing(ctx: PatchCtx, dryRun: boolean): void {
  if (!/<w:tblCellSpacing\b/.test(ctx.xml)) return;
  ctx.changes.push('Cell spacing removed');
  if (!dryRun) ctx.xml = rmTag(ctx.xml, 'tblCellSpacing');
}

function patchLayout(ctx: PatchCtx, c: Run26Config, dryRun: boolean): void {
  const r = setLayoutType(ctx.xml, c.layoutType, dryRun);
  if (!r.changed) return;
  ctx.changes.push(`Layout → ${c.layoutType}`);
  if (!dryRun) ctx.xml = r.xml;
}

function patchMargins(
  ctx: PatchCtx,
  c: Run26Config,
  marTop: string, marBottom: string, marLeft: string, marRight: string,
  dryRun: boolean
): void {
  const existing = getMarginValues(ctx.xml);
  const leftTag  = existing?.usesStartEnd ? 'start' : 'left';
  const rightTag = existing?.usesStartEnd ? 'end'   : 'right';

  const needsMargins =
    existing?.top    !== marTop    || existing?.bottom !== marBottom
    || existing?.left  !== marLeft   || existing?.right  !== marRight;

  if (!needsMargins) return;

  const was = existing
    ? `T=${twipsToInches(existing.top)}" B=${twipsToInches(existing.bottom)}" ` +
      `L=${twipsToInches(existing.left)}" R=${twipsToInches(existing.right)}"`
    : 'absent (using Word defaults)';

  ctx.changes.push(
    `Cell margins (was ${was}) → ` +
    `T=${twipsToInches(marTop)}" B=${twipsToInches(marBottom)}" ` +
    `L=${twipsToInches(marLeft)}" R=${twipsToInches(marRight)}"`
  );

  if (dryRun) return;

  const targetMar =
    `<w:tblCellMar>` +
    `<w:top w:w="${marTop}" w:type="dxa"/>` +
    `<w:${leftTag} w:w="${marLeft}" w:type="dxa"/>` +
    `<w:bottom w:w="${marBottom}" w:type="dxa"/>` +
    `<w:${rightTag} w:w="${marRight}" w:type="dxa"/>` +
    `</w:tblCellMar>`;

  if (/<w:tblCellMar\b/.test(ctx.xml)) {
    ctx.xml = ctx.xml.replace(/<w:tblCellMar\b[^>]*>[\s\S]*?<\/w:tblCellMar>/, targetMar);
  } else {
    ctx.xml = ctx.xml.replace('</w:tblPr>', `${targetMar}</w:tblPr>`);
  }
}

// S3776: patchTblPr now delegates to focused sub-functions — complexity well below 15
function patchTblPr(
  block: string,
  c: Run26Config,
  marTop: string, marBottom: string, marLeft: string, marRight: string,
  dryRun = false
): { result: string; changes: string[] } {
  const ctx: PatchCtx = { xml: block, changes: [] };

  patchWidth(ctx, c, dryRun);
  patchJc(ctx, c, dryRun);
  patchIndent(ctx, c, dryRun);
  patchWrapping(ctx, dryRun);
  patchCellSpacing(ctx, dryRun);
  patchLayout(ctx, c, dryRun);
  patchMargins(ctx, c, marTop, marBottom, marLeft, marRight, dryRun);

  return { result: ctx.xml, changes: ctx.changes };
}

function patchTrPr(
  block: string,
  tableAlignment: string,
  dryRun = false
): { result: string; changes: string[]; skipped: boolean } {
  if (/<w:tblHeader\b/.test(block)) {
    return { result: block, changes: [], skipped: true };
  }

  let xml = block;
  const ch: string[] = [];

  if (/<w:trHeight\b/.test(xml))       { ch.push('Row height removed');      if (!dryRun) xml = rmTag(xml, 'trHeight'); }
  if (/<w:tblCellSpacing\b/.test(xml)) { ch.push('Row cell spacing removed'); if (!dryRun) xml = rmTag(xml, 'tblCellSpacing'); }
  if (/<w:jc\b/.test(xml)) {
    const r = setElement(xml, 'jc', { val: tableAlignment }, '</w:trPr>', dryRun);
    if (r.changed) { ch.push(`Row alignment → ${tableAlignment}`); if (!dryRun) xml = r.xml; }
  }

  return { result: xml, changes: ch, skipped: false };
}

// ── injectNavBookmark ─────────────────────────────────────────────────────
function injectNavBookmark(xml: string, bmName: string, bmId: number): string {
  const bmXml =
    `<w:bookmarkStart w:id="${bmId}" w:name="${bmName}"/>` +
    `<w:bookmarkEnd   w:id="${bmId}"/>`;

  if (xml.includes('</w:tblPr>')) {
    return xml.replace('</w:tblPr>', `${bmXml}</w:tblPr>`);
  }
  return xml.replace(/(<w:tbl\b[^>]*>)/, `$1${bmXml}`);
}

// ─── patchTableOoxml sub-functions (S3776: reduce cognitive complexity) ───────

interface TablePatchState {
  allChanges:    string[];
  rowsPatched:   number;
  headerSkipped: number;
  rowIdx:        number;
}

function makeTblPrReplacer(
  c: Run26Config,
  marTop: string, marBottom: string, marLeft: string, marRight: string,
  dryRun: boolean,
  state: TablePatchState
): (match: string) => string {
  return (match: string): string => {
    const { result, changes } = patchTblPr(match, c, marTop, marBottom, marLeft, marRight, dryRun);
    state.allChanges.push(...changes);
    return result;
  };
}

function makeTrPrReplacer(
  tableAlignment: string,
  dryRun: boolean,
  state: TablePatchState
): (match: string) => string {
  return (match: string): string => {
    state.rowIdx++;
    const { result, changes, skipped } = patchTrPr(match, tableAlignment, dryRun);
    if (skipped) {
      state.headerSkipped++;
    } else if (changes.length > 0) {
      state.rowsPatched++;
      state.allChanges.push(...changes.map(ch => `Row ${state.rowIdx}: ${ch}`));
    }
    return result;
  };
}

// S3776: patchTableOoxml now delegates replacer logic — complexity well below 15
function patchTableOoxml(
  tableOoxml: string,
  c: Run26Config,
  marTop: string, marBottom: string, marLeft: string, marRight: string,
  dryRun = false
): { patched: string; changes: string[]; rowsPatched: number; headerSkipped: number } {
  const state: TablePatchState = { allChanges: [], rowsPatched: 0, headerSkipped: 0, rowIdx: 0 };

  let xml = rmTag(rmTag(tableOoxml, 'tblPrChange'), 'trPrChange');

  xml = xml.replace(
    /<w:tblPr\b[^>]*>[\s\S]*?<\/w:tblPr>/,
    makeTblPrReplacer(c, marTop, marBottom, marLeft, marRight, dryRun, state)
  );

  xml = xml.replace(
    /<w:trPr\b[^>]*>[\s\S]*?<\/w:trPr>/g,
    makeTrPrReplacer(c.tableAlignment, dryRun, state)
  );

  return {
    patched:       xml,
    changes:       state.allChanges,
    rowsPatched:   state.rowsPatched,
    headerSkipped: state.headerSkipped,
  };
}

async function insertComment(
  table: Word.Table, text: string, context: Word.RequestContext
): Promise<void> {
  try {
    table.rows.load('items');
    await context.sync();
    if (!table.rows.items.length) return;

    table.rows.items[0].cells.load('items');
    await context.sync();
    if (!table.rows.items[0].cells.items.length) return;

    table.rows.items[0].cells.items[0].body.paragraphs.load('items');
    await context.sync();
    if (!table.rows.items[0].cells.items[0].body.paragraphs.items.length) return;

    table.rows.items[0].cells.items[0].body.paragraphs.items[0]
      .getRange().insertComment(text);
    await context.sync();
  } catch (e) {
    console.warn(`      ⚠ Comment failed: ${(e as Error).message}`);
  }
}

/**
 * Returns true if every paragraph across every cell in the table is blank.
 * Loads rows → cells → paragraphs in one pass; bails out early as soon as
 * any non-empty text is found so it is fast for normal (non-empty) tables.
 */
async function tableIsTextEmpty(
  table: Word.Table,
  context: Word.RequestContext
): Promise<boolean> {
  table.rows.load('items');
  await context.sync();

  for (const row of table.rows.items) {
    row.cells.load('items');
    await context.sync();

    for (const cell of row.cells.items) {
      const paras = cell.body.paragraphs;
      paras.load('items');
      await context.sync();

      for (const para of paras.items) {
        para.load('text');
        await context.sync();
        if ((para.text || '').trim().length > 0) return false; // found text → not empty
      }
    }
  }

  return true; // all cells were blank
}

// ─── Main helpers (S3776: extracted from Word.run callback) ──────────────────

interface GrandTotals {
  total:         number;
  changed:       number;
  rows:          number;
  headerSkipped: number;
  emptySkipped:  number;
  textEmpty:     number;
}

function logRunHeader(cfg: ReturnType<typeof getConfig>, c: Run26Config, margins: MarginSet): void {
  console.log('═'.repeat(70));
  console.log('RUN 26: Table Defaults');
  console.log(`Profile      : ${cfg.clientName}`);
  console.log(`Font/Size    : ${c.defaultFont} ${c.defaultSize}pt`);
  console.log(`Width        : ${c.tableWidthPct / 50}% (${c.tableWidthPct} pct units)`);
  console.log(`Alignment    : ${c.tableAlignment}`);
  console.log(`Indent       : ${twipsToInches(c.tableIndentTwips)}" (${c.tableIndentTwips} twips)`);
  console.log(`Layout       : ${c.layoutType}`);
  console.log(
    `Cell margins : ` +
    `T=${twipsToInches(margins.top)}" B=${twipsToInches(margins.bottom)}" ` +
    `L=${twipsToInches(margins.left)}" R=${twipsToInches(margins.right)}"`
  );
  console.log(`OOXML mode   : ${c.ooxmlInsertMode}`);
  console.log('═'.repeat(70));
}

function reportSummary(totals: GrandTotals, navDetails: RunDetail[]): void {
  console.log('\n' + '═'.repeat(70));
  console.log(
    `Tables: ${totals.total} | Modified: ${totals.changed} | Rows patched: ${totals.rows} | ` +
    `Header rows protected: ${totals.headerSkipped} | ` +
    `Empty (0-row) skipped: ${totals.emptySkipped} | ` +
    `Empty (no text) skipped: ${totals.textEmpty}`
  );
  console.log('✓ Table Defaults complete!');
  console.log('═'.repeat(70));

  const totalSkipped = totals.emptySkipped + totals.textEmpty;

  if (totals.changed > 0) {
    reportResult('Table Defaults Complete', [
      { label: 'Tables scanned',        count: totals.total         },
      { label: 'Tables modified',       count: totals.changed       },
      { label: 'Rows modified',         count: totals.rows          },
      { label: 'Header rows protected', count: totals.headerSkipped },
      ...(totals.emptySkipped > 0
        ? [{ label: 'Empty tables skipped (0 rows)',  count: totals.emptySkipped }]
        : []
      ),
      ...(totals.textEmpty > 0
        ? [{ label: 'Empty tables skipped (no text)', count: totals.textEmpty    }]
        : []
      ),
      ...navDetails,
    ]);
  } else {
    reportResult('Table Defaults Complete', [
      {
        label: `All ${totals.total} table(s) already meet formatting standards`,
        note:  totalSkipped > 0
          ? `No changes made · ${totalSkipped} empty table(s) skipped`
          : 'No changes made',
      },
    ]);
  }
}

/** Pre-computed twip strings for the four cell margin sides. */
interface MarginSet {
  top:    string;
  bottom: string;
  left:   string;
  right:  string;
}

/** Shared run-level state threaded through section/table processing. */
interface RunCtx {
  c:          Run26Config;
  margins:    MarginSet;
  totals:     GrandTotals;
  navDetails: RunDetail[];
  bmIdBase:   number;
  bmCounter:  { value: number };
  context:    Word.RequestContext;
}

// S107: ≤7 params — patch data bundled into MarginSet; bookmark info inline
async function applyTablePatch(
  sectionBody: Word.Body,
  t: number,
  runCtx: RunCtx,
  analysis: ReturnType<typeof patchTableOoxml>,
  bmName: string,
  bmId: number
): Promise<number> {
  const { c, margins, context } = runCtx;

  const freshTables = sectionBody.tables;
  freshTables.load('items');
  await context.sync();
  if (t >= freshTables.items.length) return 0;

  const freshRange    = freshTables.items[t].getRange();
  const freshOoxmlRes = freshRange.getOoxml();
  await context.sync();

  const realPatch     = patchTableOoxml(
    freshOoxmlRes.value, c,
    margins.top, margins.bottom, margins.left, margins.right,
    false
  );
  const patchedWithBm = injectNavBookmark(realPatch.patched, bmName, bmId);

  // ooxmlInsertMode is string in config; cast here at the single call-site
  freshRange.insertOoxml(patchedWithBm, c.ooxmlInsertMode as Word.InsertLocation);
  await context.sync();
  console.log(`    ✓ Updated, nav bookmark "${bmName}" injected`);

  const commentTables = sectionBody.tables;
  commentTables.load('items');
  await context.sync();
  if (t < commentTables.items.length) {
    const commentText =
      `[QC] Table properties updated:\n` +
      analysis.changes.map(ch => `• ${ch}`).join('\n');
    await insertComment(commentTables.items[t], commentText, context);
    console.log(`    ✓ Comment added`);
  }

  return realPatch.rowsPatched;
}

// S107: ≤7 params — all run-level state in RunCtx
async function processTable(
  sectionBody: Word.Body,
  t: number,
  tableCount: number,
  runCtx: RunCtx
): Promise<boolean> {
  const { c, margins, totals, navDetails, bmIdBase, bmCounter, context } = runCtx;

  console.log(`\n  → Table ${t + 1}/${tableCount}`);

  const currentTables = sectionBody.tables;
  currentTables.load('items');
  await context.sync();
  if (t >= currentTables.items.length) return false; // signals break

  currentTables.items[t].rows.load('items');
  await context.sync();

  if (currentTables.items[t].rows.items.length === 0) {
    console.log(`    ⊘ Skipped (empty table — 0 rows)`);
    totals.emptySkipped++;
    return true;
  }

  const isEmpty = await tableIsTextEmpty(currentTables.items[t], context);
  if (isEmpty) {
    console.log(`    ⊘ Skipped (table has rows but no text content)`);
    totals.textEmpty++;
    return true;
  }

  const ooxmlResult = currentTables.items[t].getRange().getOoxml();
  await context.sync();

  const analysis = patchTableOoxml(
    ooxmlResult.value, c,
    margins.top, margins.bottom, margins.left, margins.right,
    true
  );
  totals.headerSkipped += analysis.headerSkipped;

  if (analysis.changes.length === 0) {
    console.log(`    ✓ Already correct`);
    return true;
  }

  analysis.changes.forEach(ch => console.log(`    • ${ch}`));

  const bmName = `${SRS_NAV_PREFIX}r26_${bmIdBase}_${bmCounter.value}`;
  const bmId   = bmIdBase + bmCounter.value;
  bmCounter.value++;

  const rowsPatched = await applyTablePatch(sectionBody, t, runCtx, analysis, bmName, bmId);

  totals.changed++;
  totals.rows += rowsPatched;
  navDetails.push(rangeDetail(bmName, `Table ${t + 1}`));
  return true;
}

// S107: ≤7 params — all run-level state in RunCtx
async function processSection(
  sectionBody: Word.Body,
  sectionIndex: number,
  runCtx: RunCtx
): Promise<void> {
  console.log(`\n── Section ${sectionIndex + 1} ──`);

  const tables = sectionBody.tables;
  tables.load('items');
  await runCtx.context.sync();

  const tableCount = tables.items.length;
  if (tableCount === 0) { console.log('  No tables'); return; }

  console.log(`  Tables: ${tableCount}`);
  runCtx.totals.total += tableCount;

  for (let t = 0; t < tableCount; t++) {
    const shouldContinue = await processTable(sectionBody, t, tableCount, runCtx);
    if (!shouldContinue) break;
  }
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export async function run26() {
  const cfg = getConfig();
  const c: Run26Config = cfg.run26_tableDefaults;

  const margins: MarginSet = {
    top:    ptToTwips(c.cellPadding.top),
    bottom: ptToTwips(c.cellPadding.bottom),
    left:   ptToTwips(c.cellPadding.left),
    right:  ptToTwips(c.cellPadding.right),
  };

  const bmIdBase  = Date.now() & 0x0FFFFFFF;
  const bmCounter = { value: 0 };

  // S3776: Word.run callback now delegates entirely to named helpers
  await Word.run(async (context) => {
    try {
      logRunHeader(cfg, c, margins);

      const sections = context.document.sections;
      sections.load('items');
      await context.sync();

      const runCtx: RunCtx = {
        c,
        margins,
        totals:     { total: 0, changed: 0, rows: 0, headerSkipped: 0, emptySkipped: 0, textEmpty: 0 },
        navDetails: [],
        bmIdBase,
        bmCounter,
        context,
      };

      for (let s = 0; s < sections.items.length; s++) {
        await processSection(sections.items[s].body, s, runCtx);
      }

      reportSummary(runCtx.totals, runCtx.navDetails);

    } catch (error) {
      console.error('Error in run26:', error);
      console.error('Message:', (error as Error).message);
      safeAlert('An error occurred during Table Defaults.\nCheck console (F12) for details.');
    }
  });
}